import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { getSoftRooms, createSoftRoom, unlockSafe } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'
import { Building2, Lock, Gift, Backpack, Plus } from 'lucide-react'

export default function SoftRooms() {
  const [showForm, setShowForm] = useState(false)
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { data: rooms, isLoading } = useQuery({
    queryKey: ['softRooms'],
    queryFn: getSoftRooms,
  })

  const createMutation = useMutation({
    mutationFn: createSoftRoom,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['softRooms'] })
      queryClient.invalidateQueries({ queryKey: ['stats'] })
      setShowForm(false)
      toast({ title: 'Soft room created successfully' })
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to create soft room', description: error.message, variant: 'destructive' })
    },
  })

  const handleUnlockSafe = async (roomId: string, _roomName: string) => {
    try {
      const result = await unlockSafe(roomId)
      toast({
        title: 'Safe Unlocked',
        description: `Temporary code: ${result.temporary_code} (expires in ${result.expires_in_minutes} min)`,
      })
    } catch (error: any) {
      toast({
        title: 'Failed to unlock safe',
        description: error.message,
        variant: 'destructive',
      })
    }
  }

  const handleCreate = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const data = {
      name: formData.get('name'),
      airport_code: formData.get('airport_code'),
      terminal: formData.get('terminal'),
      phone: formData.get('phone') || undefined,
      has_safe: formData.get('has_safe') === 'on',
      gift_cards_available: parseInt(formData.get('gift_cards_available') as string) || 0,
      backpacks_available: parseInt(formData.get('backpacks_available') as string) || 0,
    }
    createMutation.mutate(data)
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-foreground">Soft Rooms</h1>
        <Button size="sm" onClick={() => setShowForm(!showForm)} className="gap-1.5">
          <Plus className="h-3.5 w-3.5" />
          New Soft Room
        </Button>
      </div>

      {/* Create Soft Room Form */}
      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>Create Soft Room</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Room Name *</label>
                  <input
                    name="name"
                    required
                    className="w-full px-3 py-2 border rounded-lg bg-secondary border-border text-sm"
                    placeholder="e.g. UA Soft Room 1"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Airport Code *</label>
                  <input
                    name="airport_code"
                    required
                    className="w-full px-3 py-2 border rounded-lg bg-secondary border-border text-sm"
                    placeholder="e.g. IAH"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Terminal</label>
                  <input
                    name="terminal"
                    className="w-full px-3 py-2 border rounded-lg bg-secondary border-border text-sm"
                    placeholder="e.g. Terminal E"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Phone</label>
                  <input
                    name="phone"
                    className="w-full px-3 py-2 border rounded-lg bg-secondary border-border text-sm"
                    placeholder="(555) 000-0000"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="has_safe" defaultChecked className="rounded" />
                    <span className="text-sm">Has Safe</span>
                  </label>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Gift Cards Available</label>
                  <input
                    type="number"
                    name="gift_cards_available"
                    min="0"
                    defaultValue="0"
                    className="w-full px-3 py-2 border rounded-lg bg-secondary border-border text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Backpacks Available</label>
                  <input
                    type="number"
                    name="backpacks_available"
                    min="0"
                    defaultValue="0"
                    className="w-full px-3 py-2 border rounded-lg bg-secondary border-border text-sm"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Creating...' : 'Create Soft Room'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {rooms && rooms.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rooms.map((room: any) => (
            <Card key={room.id}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  {room.name}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-sm text-muted-foreground">
                  <p>
                    <strong>Location:</strong> {room.airport_code} - {room.terminal}
                  </p>
                  {room.phone && (
                    <p>
                      <strong>Phone:</strong> {room.phone}
                    </p>
                  )}
                </div>

                {/* Inventory */}
                <div className="flex gap-4 pt-2 border-t">
                  <div className="flex items-center gap-2">
                    <Gift className="h-4 w-4 text-chart-3" />
                    <span className="text-sm">
                      {room.gift_cards_available} gift cards
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Backpack className="h-4 w-4 text-primary" />
                    <span className="text-sm">
                      {room.backpacks_available} backpacks
                    </span>
                  </div>
                </div>

                {/* Safe Access */}
                {room.has_safe && (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => handleUnlockSafe(room.id, room.name)}
                  >
                    <Lock className="h-4 w-4 mr-2" />
                    Unlock Safe
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Building2 className="h-10 w-10 mb-3 opacity-40" />
            <p className="font-medium">No soft rooms yet</p>
            <p className="text-sm mt-1">Create one to get started</p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
